import { useState, useEffect, useCallback } from "react";
import GetUserInfoService from "../../../../../service/GetUserInfoService";

const service = new GetUserInfoService();

function formatDateYYYYMMDD(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDefaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { dateBegin: formatDateYYYYMMDD(start), dateEnd: formatDateYYYYMMDD(end) };
}

const isSuccess = (res) => res?.messageCode === 200000 || res?.messageCode === 200001;

export function useEconomicCircleData(options = {}) {
  const { fetchTransactions = true } = options;
  const [data, setData] = useState({
    rules: null,
    balance: null,
    directs: [],
    all: [],
    recentTransactions: [],
    extracoinUsage: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    const token = localStorage.getItem("__extraconomy_token");
    const username = localStorage.getItem("__extraconomy_username");
    if (!token || !username) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [rulesRes, balanceRes, directsRes, allRes, percentUsageRes] = await Promise.allSettled([
        service.getUserRules(token, username),
        service.getWalletExtrabonus(token, username),
        service.getNetworkDirectsInfo(token, username),
        service.getNetworkAllInfo(token, username),
        service.getExtracoinPercentage(token, username),
      ]);

      const directs = directsRes.status === "fulfilled" && isSuccess(directsRes.value)
        ? (directsRes.value.result || [])
        : [];
      const all = allRes.status === "fulfilled" && isSuccess(allRes.value)
        ? (allRes.value.result || [])
        : [];
      const rules = rulesRes.status === "fulfilled" && isSuccess(rulesRes.value)
        ? rulesRes.value.result
        : null;
      const balance = balanceRes.status === "fulfilled" && isSuccess(balanceRes.value)
        ? balanceRes.value.result
        : null;

      const extracoinUsage =
        percentUsageRes.status === "fulfilled" && isSuccess(percentUsageRes.value)
          ? percentUsageRes.value.result ?? null
          : null;

      let recentTransactions = [];
      if (fetchTransactions) {
        const { dateBegin, dateEnd } = getDefaultDateRange();
        try {
          const txRes = await service.getExtrabonusTransactions(
            token,
            username,
            dateBegin,
            dateEnd
          );
          const list = Array.isArray(txRes?.result) ? txRes.result : [];
          recentTransactions = list.slice(0, 6);
        } catch {
          recentTransactions = [];
        }
      }

      setData({
        rules,
        balance,
        directs,
        all,
        recentTransactions,
        extracoinUsage,
      });
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchTransactions]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, isLoading, error, refresh: fetchAll };
}

export function useEconomicCircleTransactions(dateBegin, dateEnd) {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTransactions = useCallback(async () => {
    const token = localStorage.getItem("__extraconomy_token");
    const username = localStorage.getItem("__extraconomy_username");
    if (!token || !username) return;
    setIsLoading(true);
    setError(null);
    try {
      const start = dateBegin || formatDateYYYYMMDD(new Date(Date.now() - 30 * 86400000));
      const end = dateEnd || formatDateYYYYMMDD(new Date());
      const res = await service.getExtrabonusTransactions(token, username, start, end);
      setTransactions(Array.isArray(res?.result) ? res.result : []);
    } catch (err) {
      setError(err);
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateBegin, dateEnd]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return { transactions, isLoading, error, refresh: fetchTransactions };
}
